import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO = 'https://customer-assets.emergentagent.com/job_egg-inventory-pro/artifacts/1fdjtarl_Green%20Label%20with%20no%20Logo.jpeg';

const formatDetail = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const inputCls = 'w-full h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 focus:border-emerald-600';

const Register = () => {
  const { role } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('k') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [roleLabel, setRoleLabel] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    last_name: '', first_name: '', phone: '', email: '', username: '', password: '', confirm: ''
  });

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/registration-links/verify?role=${encodeURIComponent(role)}&k=${encodeURIComponent(token)}`);
        setRoleLabel(res.data.label);
        setValid(true);
      } catch (e) {
        setValid(false);
      } finally {
        setChecking(false);
      }
    };
    verify();
  }, [role, token]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.last_name.trim() || !form.first_name.trim()) return toast.error('Surname and First Name are required');
    if (!form.phone.trim()) return toast.error('Phone number is required');
    if (!form.username.trim()) return toast.error('Please choose a username');
    if (form.password.length < 4) return toast.error('Password must be at least 4 characters');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register-role`, {
        role, token,
        first_name: form.first_name,
        last_name: form.last_name,
        phone: form.phone,
        email: form.email || undefined,
        username: form.username,
        password: form.password,
      });
      setSubmitted(true);
    } catch (error) {
      toast.error(formatDetail(error.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="invalid-link">
          <AlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Invalid Registration Link</h1>
          <p className="text-slate-600 mt-2">This link is invalid or has expired. Please request a fresh registration link from the farm administrator.</p>
          <Link to="/login" className="inline-block mt-6 text-emerald-700 font-medium hover:underline">Go to Login</Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="registration-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">Registration Submitted</h1>
          <p className="text-slate-600 mt-3">
            Thank you, <span className="font-medium">{form.first_name}</span>. Your <span className="font-semibold text-emerald-700">{roleLabel}</span> account has been created and is now <span className="font-medium">pending admin approval</span>.
          </p>
          <p className="text-slate-500 text-sm mt-2">You'll be able to log in with your chosen username and password once an administrator approves your account.</p>
          <Link to="/login" className="inline-block mt-6 px-5 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg p-8">
        <div className="text-center mb-6">
          <img src={LOGO} alt="Wilyrash Farm" className="w-24 h-24 mx-auto mb-4 rounded-full object-cover" />
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Wilyrash Farm</h1>
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-semibold" data-testid="register-role-label">
            Registering as: {roleLabel}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Last / Surname <span className="text-red-500">*</span></label>
              <input className={inputCls} data-testid="reg-last-name" value={form.last_name} onChange={e => set('last_name', e.target.value)} placeholder="Surname" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name <span className="text-red-500">*</span></label>
              <input className={inputCls} data-testid="reg-first-name" value={form.first_name} onChange={e => set('first_name', e.target.value)} placeholder="First name" required />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number <span className="text-red-500">*</span></label>
            <input type="tel" className={inputCls} data-testid="reg-phone" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="e.g. 0803..." required />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address <span className="text-slate-400">(optional)</span></label>
            <input type="email" className={inputCls} data-testid="reg-email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="you@example.com" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Choose a Username <span className="text-red-500">*</span></label>
            <input className={inputCls} data-testid="reg-username" value={form.username} onChange={e => set('username', e.target.value)} placeholder="Pick a username to log in with" required />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Choose a Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} className={`${inputCls} pr-10`} data-testid="reg-password" value={form.password} onChange={e => set('password', e.target.value)} required />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
              <input type={showPassword ? 'text' : 'password'} className={inputCls} data-testid="reg-confirm" value={form.confirm} onChange={e => set('confirm', e.target.value)} required />
            </div>
          </div>

          <p className="text-xs text-slate-500">Your password is stored securely and is never visible to administrators. Your account will require admin approval before you can log in.</p>

          <button type="submit" disabled={loading} className="w-full h-11 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-60 flex items-center justify-center gap-2" data-testid="reg-submit">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Submitting...' : 'Submit Registration'}
          </button>

          <p className="text-center text-sm text-slate-500">
            Already have an account? <Link to="/login" className="text-emerald-700 font-medium hover:underline">Log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default Register;
